import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Alert,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  Snackbar,
} from '@mui/material';
import {
  Search,
  Refresh,
  Visibility,
  Block,
  Person,
  ShoppingCart,
  Email,
  Phone,
  LocationOn,
  Business,
  AccountBalance,
  CreditCard,
  Edit,
  Delete,
  Receipt,
  PersonAdd,
  ContentCopy,
  Download,
  CheckCircle,
  Cancel as CancelIcon,
  SwapHoriz,
  Send,
} from '@mui/icons-material';
import { useBackofficeAuth } from '../../hooks/useBackofficeAuth';
import { getBackofficeAuthHeaders } from '../../services/api';
import { API_BASE_URL } from '../../config/constants';
import { useInvoice } from '../../hooks/useInvoice';
import { useBackofficeInvoice } from '../../hooks/useBackofficeInvoice';
import { backofficeManagementService } from '../../services/backofficeManagementService';
import SyncStatusCell from '../../components/backoffice/SyncStatusCell';
import CustomerContactsTab from '../../components/backoffice/CustomerContactsTab';

interface Customer {
  id: string;
  company_name: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  epicor_customer_id?: string;
  mowilex_customer_id?: string;
  province?: string;
  city?: string;
  territory?: string;
  is_approved: boolean;
  is_active?: boolean;
  credit_limit: number;
  outstanding_balance: number;
  available_credit: number;
  customer_group?: string;
  portal_status?: 'active' | 'pending' | 'none';
  created_at: string;
  last_sync_at?: string;
  epicor_sync_status?: string | null;
  // Assigned sales rep resolved from the workforce cache.
  sales_rep_code?: string | null;
  sales_rep?: {
    code: string;
    name: string | null;
    email: string | null;
    role_code: string | null;
    role_description: string | null;
    is_active: boolean;
  } | null;
}

interface ActivationResult {
  success: boolean;
  message: string;
  customer_id: string;
  user_id: string;
  temporary_password: string;
  activation_url: string;
  activation_token: string;
  email_sent: boolean;
}

interface CustomerUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
  last_login_at?: string;
  created_at: string;
}

interface CustomerOrder {
  id: string;
  order_number: string;
  status: string;
  total_amount: number;
  order_date: string;
  delivery_date?: string;
  item_count: number;
  epicor_sync_status?: string | null;
  epicor_order_num?: number | string | null;
  last_sync_attempt_at?: string | null;
  last_sync_success_at?: string | null;
  sync_error_message?: string | null;
  // 'portal' = placed via the customer portal (Online).
  // 'epicor' = imported from Epicor (Offline / sales-rep placed).
  created_via?: 'portal' | 'epicor' | null;
  sales_rep_code?: string | null;
  sales_rep_name?: string | null;
}

interface OrderItem {
  id: string;
  product_id: string;
  product_name: string;
  product_code: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-detail-tabpanel-${index}`}
      aria-labelledby={`customer-detail-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const CustomerManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useBackofficeAuth();
  const { getInvoice, generateInvoice, downloadInvoice, isLoading: invoiceLoading, isGenerating, error: invoiceError, infoMessage: invoiceInfoMessage } = useInvoice();
  const { 
    getCustomerInvoices, 
    getOrderInvoice: getBackofficeOrderInvoice,
    generateOrderInvoice: generateBackofficeOrderInvoice,
    downloadInvoice: downloadBackofficeInvoice,
    isLoading: backofficeInvoiceLoading,
    isGenerating: backofficeIsGenerating,
    error: backofficeInvoiceError,
    infoMessage: backofficeInvoiceInfoMessage,
    invoicesList: customerInvoicesList
  } = useBackofficeInvoice();
  
  // State for customer list
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [provinceFilter, setProvinceFilter] = useState<string>('all');
  const [cityFilter, setCityFilter] = useState<string>('all');
  
  // Available filter options
  const [provinces, setProvinces] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);

  // New filter state for portal status, sort, territory, customer group
  const [portalStatus, setPortalStatus] = useState<string>('all');
  const [exporting, setExporting] = useState(false);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<string>('desc');
  const [territories, setTerritories] = useState<string[]>([]);
  const [customerGroups, setCustomerGroups] = useState<string[]>([]);
  const [selectedTerritory, setSelectedTerritory] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState<string>('');

  // Activation dialog state
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activateCustomer, setActivateCustomer] = useState<Customer | null>(null);
  const [activateEmail, setActivateEmail] = useState('');
  const [activateFullName, setActivateFullName] = useState('');
  const [activatePhone, setActivatePhone] = useState('');
  const [activateDeliveryMethod, setActivateDeliveryMethod] = useState<'email' | 'whatsapp' | 'manual'>('email');
  const [activateCustomMessage, setActivateCustomMessage] = useState('');
  const [activateAdminNotes, setActivateAdminNotes] = useState('');
  const [activating, setActivating] = useState(false);

  // Activation result dialog state
  const [activationResult, setActivationResult] = useState<ActivationResult | null>(null);
  const [activationResultDialogOpen, setActivationResultDialogOpen] = useState(false);

  // Resend activation state
  const [resending, setResending] = useState(false);
  const [resendResultDialogOpen, setResendResultDialogOpen] = useState(false);
  const [resendResult, setResendResult] = useState<{ activation_url: string; email_sent: boolean } | null>(null);

  // Change main contact dialog state
  const [changeContactDialogOpen, setChangeContactDialogOpen] = useState(false);
  const [changeContactCustomer, setChangeContactCustomer] = useState<Customer | null>(null);
  const [changeContactEmail, setChangeContactEmail] = useState('');
  const [changeContactFullName, setChangeContactFullName] = useState('');
  const [changeContactPhone, setChangeContactPhone] = useState('');
  const [changingContact, setChangingContact] = useState(false);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Customer detail dialog state
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailTabValue, setDetailTabValue] = useState(0);
  
  // Customer detail data
  const [customerUsers, setCustomerUsers] = useState<CustomerUser[]>([]);
  const [customerOrders, setCustomerOrders] = useState<CustomerOrder[]>([]);
  // Client-side filter for the Orders tab — server already returns
  // both sources together; this lets staff narrow the view to one
  // without re-fetching.
  const [customerOrdersSourceFilter, setCustomerOrdersSourceFilter] = useState<'all' | 'portal' | 'epicor'>('all');
  const [customerInvoices, setCustomerInvoices] = useState<any[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Invoice management state
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState<string>('all');
  
  // Order items dialog state
  const [selectedOrder, setSelectedOrder] = useState<CustomerOrder | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [orderItemsDialogOpen, setOrderItemsDialogOpen] = useState(false);
  const [orderItemsLoading, setOrderItemsLoading] = useState(false);

  // Load customers with filters
  const loadCustomers = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        size: rowsPerPage.toString(),
      });

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      if (provinceFilter !== 'all') {
        params.append('province', provinceFilter);
      }

      if (cityFilter !== 'all') {
        params.append('city', cityFilter);
      }

      if (portalStatus !== 'all') {
        params.append('portal_status', portalStatus);
      }

      if (sortBy) {
        params.append('sort_by', sortBy);
      }

      if (sortOrder) {
        params.append('sort_order', sortOrder);
      }

      if (selectedTerritory) {
        params.append('territory', selectedTerritory);
      }

      if (selectedGroup) {
        params.append('customer_group', selectedGroup);
      }

      const response = await fetch(`${API_BASE_URL}/backoffice/customers?${params}`, {
        headers: {
          ...getBackofficeAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load customers: ${response.statusText}`);
      }

      const data = await response.json();
      setCustomers(data.items || []);
      setTotalCount(data.total || 0);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, page, rowsPerPage, searchTerm, statusFilter, provinceFilter, cityFilter, portalStatus, sortBy, sortOrder, selectedTerritory, selectedGroup]);

  // Build the same filter params the grid is showing. Pagination is
  // deliberately left out -- the export is every matching customer, not
  // the page on screen.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (searchTerm.trim()) params.append('search', searchTerm.trim());
    if (statusFilter !== 'all') params.append('status', statusFilter);
    if (provinceFilter !== 'all') params.append('province', provinceFilter);
    if (cityFilter !== 'all') params.append('city', cityFilter);
    if (portalStatus !== 'all') params.append('portal_status', portalStatus);
    if (sortBy) params.append('sort_by', sortBy);
    if (sortOrder) params.append('sort_order', sortOrder);
    if (selectedTerritory) params.append('territory', selectedTerritory);
    if (selectedGroup) params.append('customer_group', selectedGroup);
    return params;
  }, [searchTerm, statusFilter, provinceFilter, cityFilter, portalStatus, sortBy, sortOrder, selectedTerritory, selectedGroup]);

  const handleExportExcel = useCallback(async () => {
    setExporting(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/backoffice/customers/export?${buildFilterParams()}`,
        { headers: { ...getBackofficeAuthHeaders() } },
      );

      if (!response.ok) {
        throw new Error(`Failed to export customers: ${response.statusText}`);
      }

      // Prefer the server's filename (it encodes the active filter and a
      // timestamp); fall back if the header isn't exposed by CORS.
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `pelanggan-${new Date().toISOString().slice(0, 10)}.xlsx`;

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      const rows = response.headers.get('X-Total-Rows');
      setSnackbarMessage(
        rows
          ? t('backoffice.customerManagement.export.success', { total: Number(rows) })
          : t('backoffice.customerManagement.export.successNoCount'),
      );
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error exporting customers:', err);
      setError(err instanceof Error ? err.message : t('backoffice.customerManagement.export.failed'));
    } finally {
      setExporting(false);
    }
  }, [buildFilterParams, t]);

  // Load filter options
  const loadFilterOptions = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const data = await backofficeManagementService.getCustomerFilterOptions();
      setProvinces(data.provinces || []);
      setCities(data.cities || []);
      setTerritories(data.territories || []);
      setCustomerGroups(data.customer_groups || []);
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  }, [isAuthenticated]);

  // Load customer details (users and orders)
  const loadCustomerDetails = async (customerId: string) => {
    setDetailLoading(true);
    try {
      // Load customer users
      const usersResponse = await fetch(`${API_BASE_URL}/backoffice/customers/${customerId}/users`, {
        headers: {
          ...getBackofficeAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });
      
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setCustomerUsers(usersData);
      }

      // Load customer orders
      const ordersResponse = await fetch(`${API_BASE_URL}/backoffice/customers/${customerId}/orders`, {
        headers: {
          ...getBackofficeAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });
      
      if (ordersResponse.ok) {
        const ordersData = await ordersResponse.json();
        setCustomerOrders(ordersData);
      }

      // Load customer invoices using the new hook
      const invoicesData = await getCustomerInvoices(customerId, 1, 50, invoiceStatusFilter);
      if (invoicesData) {
        setCustomerInvoices(invoicesData.items);
      }
    } catch (err) {
      console.error('Error loading customer details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (isAuthenticated) {
      loadCustomers();
      loadFilterOptions();
    }
  }, [loadCustomers, loadFilterOptions, isAuthenticated]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 0) {
        loadCustomers();
      } else {
        setPage(0); // Reset to first page when searching
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, provinceFilter, cityFilter, portalStatus, sortBy, sortOrder, selectedTerritory, selectedGroup]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [statusFilter, provinceFilter, cityFilter, portalStatus, sortBy, sortOrder, selectedTerritory, selectedGroup]);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDetailDialogOpen(true);
    setDetailTabValue(0);
    loadCustomerDetails(customer.id);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedCustomer(null);
    setCustomerUsers([]);
    setCustomerOrders([]);
    setCustomerOrdersSourceFilter('all');
    setCustomerInvoices([]);
  };

  const handleBlockCustomer = async (customer: Customer) => {
    try {
      const response = await fetch(`${API_BASE_URL}/backoffice/customers/${customer.id}/block`, {
        method: 'POST',
        headers: {
          ...getBackofficeAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        loadCustomers(); // Refresh the list
      }
    } catch (err) {
      console.error('Error blocking customer:', err);
    }
  };

  const handleBlockUser = async (userId: string) => {
    if (!selectedCustomer) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/backoffice/customers/${selectedCustomer.id}/users/${userId}/block`, {
        method: 'POST',
        headers: {
          ...getBackofficeAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Refresh the customer users list
        loadCustomerDetails(selectedCustomer.id);
      }
    } catch (err) {
      console.error('Error blocking user:', err);
    }
  };

  // Activation handlers
  const handleOpenActivateDialog = (customer: Customer) => {
    setActivateCustomer(customer);
    setActivateEmail(customer.email || '');
    setActivateFullName(customer.contact_person || '');
    setActivatePhone(customer.phone || '');
    setActivateDeliveryMethod('email');
    setActivateCustomMessage('');
    setActivateAdminNotes('');
    setActivateDialogOpen(true);
  };

  const handleCloseActivateDialog = () => {
    setActivateDialogOpen(false);
    setActivateCustomer(null);
  };

  const handleActivateCustomer = async () => {
    if (!activateCustomer) return;

    setActivating(true);
    try {
      const result = await backofficeManagementService.activateCustomer(activateCustomer.id, {
        email: activateEmail,
        full_name: activateFullName,
        phone: activatePhone || undefined,
        delivery_method: activateDeliveryMethod,
        custom_message: activateCustomMessage || undefined,
        admin_notes: activateAdminNotes || undefined,
      });

      setActivationResult(result);
      setActivateDialogOpen(false);
      setActivationResultDialogOpen(true);
      loadCustomers(); // Refresh list
    } catch (err) {
      console.error('Error activating customer:', err);
      setError(err instanceof Error ? err.message : t('backoffice.customerManagement.activation.activationFailed'));
    } finally {
      setActivating(false);
    }
  };

  const handleCloseActivationResult = () => {
    setActivationResultDialogOpen(false);
    setActivationResult(null);
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbarMessage(t('backoffice.customerManagement.activation.copied'));
      setSnackbarOpen(true);
    });
  };

  const handleDownloadActivationInfo = () => {
    if (!activationResult || !activateCustomer) return;

    const content = [
      `Customer Activation Details`,
      `===========================`,
      `Company: ${activateCustomer.company_name}`,
      `Epicor ID: ${activateCustomer.epicor_customer_id || 'N/A'}`,
      `Email: ${activateEmail}`,
      `Full Name: ${activateFullName}`,
      ``,
      `Activation URL: ${activationResult.activation_url}`,
      `Temporary Password: ${activationResult.temporary_password}`,
      ``,
      `Email Sent: ${activationResult.email_sent ? 'Yes' : 'No'}`,
      `Activated: ${new Date().toLocaleString()}`,
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activation-${activateCustomer.company_name.replace(/\s+/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Resend activation link handler
  const handleResendLink = async (customer: Customer) => {
    setResending(true);
    try {
      const result = await backofficeManagementService.resendActivation(customer.id, 'email');
      setResendResult(result);
      setResendResultDialogOpen(true);
      setSnackbarMessage(t('backoffice.customerManagement.activation.resendSuccess'));
      setSnackbarOpen(true);
      loadCustomers();
    } catch (err) {
      console.error('Error resending activation:', err);
      setError(err instanceof Error ? err.message : 'Failed to resend activation link');
    } finally {
      setResending(false);
    }
  };

  // Change main contact handlers
  const handleOpenChangeContactDialog = (customer: Customer) => {
    setChangeContactCustomer(customer);
    setChangeContactEmail(customer.email || '');
    setChangeContactFullName(customer.contact_person || '');
    setChangeContactPhone(customer.phone || '');
    setChangeContactDialogOpen(true);
  };

  const handleCloseChangeContactDialog = () => {
    setChangeContactDialogOpen(false);
    setChangeContactCustomer(null);
  };

  const handleChangeMainContact = async () => {
    if (!changeContactCustomer) return;

    setChangingContact(true);
    try {
      await backofficeManagementService.changeMainContact(changeContactCustomer.id, {
        email: changeContactEmail,
        full_name: changeContactFullName,
        phone: changeContactPhone || undefined,
      });
      setChangeContactDialogOpen(false);
      setSnackbarMessage(t('backoffice.customerManagement.activation.contactChanged'));
      setSnackbarOpen(true);
      loadCustomers();
    } catch (err) {
      console.error('Error changing main contact:', err);
      setError(err instanceof Error ? err.message : 'Failed to change main contact');
    } finally {
      setChangingContact(false);
    }
  };

  // Combined status + action rendering for portal status column
  const getStatusAndAction = (customer: Customer) => {
    if (customer.is_active === false) {
      return <Chip label={t('backoffice.customerManagement.filters.blocked')} color="error" size="small" />;
    }

    const portalSt = customer.portal_status || 'none';

    if (portalSt === 'active') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={t('backoffice.customerManagement.activation.portalActive')} color="success" size="small" />
        </Box>
      );
    }

    if (portalSt === 'pending') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={t('backoffice.customerManagement.activation.portalPending')} color="warning" size="small" />
          <Button
            size="small"
            variant="text"
            color="primary"
            disabled={resending}
            onClick={(e) => { e.stopPropagation(); handleResendLink(customer); }}
            sx={{ fontSize: '0.7rem', textTransform: 'none', minWidth: 'auto' }}
            startIcon={resending ? <CircularProgress size={12} /> : <Send />}
          >
            {t('backoffice.customerManagement.activation.resend')}
          </Button>
        </Box>
      );
    }

    // portal_status = "none"
    return (
      <Button
        variant="outlined"
        size="small"
        color="primary"
        startIcon={<PersonAdd />}
        onClick={(e) => {
          e.stopPropagation();
          handleOpenActivateDialog(customer);
        }}
        sx={{ textTransform: 'none' }}
      >
        {t('backoffice.customerManagement.activation.activate')}
      </Button>
    );
  };

  // Load order items
  const loadOrderItems = async (orderId: string) => {
    setOrderItemsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/backoffice/orders/${orderId}/items`, {
        headers: {
          ...getBackofficeAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrderItems(data.items || []);
      } else {
        console.error('Failed to load order items');
        setOrderItems([]);
      }
    } catch (error) {
      console.error('Error loading order items:', error);
      setOrderItems([]);
    } finally {
      setOrderItemsLoading(false);
    }
  };

  const handleViewOrder = (order: CustomerOrder) => {
    setSelectedOrder(order);
    setOrderItemsDialogOpen(true);
    loadOrderItems(order.id);
  };

  const handleEditOrder = async (order: CustomerOrder) => {
    // This could open a status update dialog
    const newStatus = prompt(`Change order status from ${order.status}:`, order.status);
    if (newStatus && newStatus !== order.status) {
      try {
        const response = await fetch(`${API_BASE_URL}/backoffice/orders/${order.id}/status`, {
          method: 'PUT',
          headers: {
            ...getBackofficeAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: newStatus }),
        });

        if (response.ok) {
          // Refresh the customer orders list
          if (selectedCustomer) {
            loadCustomerDetails(selectedCustomer.id);
          }
        }
      } catch (err) {
        console.error('Error updating order status:', err);
      }
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/backoffice/orders/${orderId}`, {
        method: 'DELETE',
        headers: {
          ...getBackofficeAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Refresh the customer orders list
        if (selectedCustomer) {
          loadCustomerDetails(selectedCustomer.id);
        }
      }
    } catch (err) {
      console.error('Error cancelling order:', err);
    }
  };

  // Invoice functionality
  const isInvoiceAvailable = (order: CustomerOrder): boolean => {
    return order.status === 'INVOICED' || order.status === 'CLOSED';
  };

  const handleDownloadInvoice = async (orderId: string) => {
    try {
      // Use the new backoffice invoice functionality
      const invoiceResponse = await getBackofficeOrderInvoice(orderId);
      
      if (invoiceResponse && invoiceResponse.success && invoiceResponse.invoice_id) {
        // Invoice exists, download it
        await downloadBackofficeInvoice(invoiceResponse.invoice_id);
      } else {
        // No invoice exists, generate one
        const generateResponse = await generateBackofficeOrderInvoice(orderId);
        if (generateResponse && generateResponse.success && generateResponse.invoice_id) {
          await downloadBackofficeInvoice(generateResponse.invoice_id);
        } else {
          console.error('Failed to generate invoice:', generateResponse?.message);
        }
      }
    } catch (error) {
      console.error('Error handling invoice download:', error);
    }
  };

  const handleDownloadInvoiceById = async (invoiceId: string) => {
    try {
      await downloadBackofficeInvoice(invoiceId);
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  const handleGenerateInvoiceFromOrder = async (orderId: string) => {
    try {
      const generateResponse = await generateBackofficeOrderInvoice(orderId, true); // Force regenerate
      if (generateResponse && generateResponse.success) {
        // Reload customer details to refresh invoices list
        if (selectedCustomer) {
          loadCustomerDetails(selectedCustomer.id);
        }
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{t('backoffice.customerManagement.messages.loginRequired')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('backoffice.customerManagement.title')}
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1, minWidth: 300 }}>
            <TextField
              fullWidth
              label={t('backoffice.customerManagement.search')}
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('backoffice.customerManagement.searchPlaceholder')}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
          </Box>
          
          <Box sx={{ minWidth: 150 }}>
            <FormControl fullWidth>
              <InputLabel>{t('backoffice.customerManagement.filters.status')}</InputLabel>
              <Select
                value={statusFilter}
                label={t('backoffice.customerManagement.filters.status')}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">{t('backoffice.customerManagement.filters.all')}</MenuItem>
                <MenuItem value="active">{t('backoffice.customerManagement.filters.active')}</MenuItem>
                <MenuItem value="pending">{t('backoffice.customerManagement.filters.pending')}</MenuItem>
                <MenuItem value="blocked">{t('backoffice.customerManagement.filters.blocked')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ minWidth: 150 }}>
            <FormControl fullWidth>
              <InputLabel>{t('backoffice.customerManagement.filters.province')}</InputLabel>
              <Select
                value={provinceFilter}
                label={t('backoffice.customerManagement.filters.province')}
                onChange={(e) => setProvinceFilter(e.target.value)}
              >
                <MenuItem value="all">{t('backoffice.customerManagement.filters.allProvinces')}</MenuItem>
                {provinces.map((province) => (
                  <MenuItem key={province} value={province}>
                    {province}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ minWidth: 150 }}>
            <FormControl fullWidth>
              <InputLabel>{t('backoffice.customerManagement.filters.city')}</InputLabel>
              <Select
                value={cityFilter}
                label={t('backoffice.customerManagement.filters.city')}
                onChange={(e) => setCityFilter(e.target.value)}
              >
                <MenuItem value="all">{t('backoffice.customerManagement.filters.allCities')}</MenuItem>
                {cities.map((city) => (
                  <MenuItem key={city} value={city}>
                    {city}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ minWidth: 120 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadCustomers}
              disabled={loading}
              fullWidth
            >
              Refresh
            </Button>
          </Box>

          <Box sx={{ minWidth: 190 }}>
            <Tooltip title={t('backoffice.customerManagement.export.tooltip')}>
              <span>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <Download />}
                  onClick={handleExportExcel}
                  disabled={exporting || loading || totalCount === 0}
                  fullWidth
                >
                  {exporting
                    ? t('backoffice.customerManagement.export.inProgress')
                    : t('backoffice.customerManagement.export.button')}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Second row: Portal Status, Sort, Territory, Customer Group */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
          {/* Portal Status Chips */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {t('backoffice.customerManagement.filters.portalStatus')}
            </Typography>
            <ToggleButtonGroup
              value={portalStatus}
              exclusive
              onChange={(_, value) => { if (value !== null) setPortalStatus(value); }}
              size="small"
            >
              <ToggleButton value="all">{t('backoffice.customerManagement.filters.portalAll')}</ToggleButton>
              <ToggleButton value="active">{t('backoffice.customerManagement.filters.portalActive')}</ToggleButton>
              <ToggleButton value="pending">{t('backoffice.customerManagement.filters.portalPending')}</ToggleButton>
              <ToggleButton value="none">{t('backoffice.customerManagement.filters.portalNone')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Sort */}
          <Box sx={{ minWidth: 200 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('backoffice.customerManagement.filters.sortBy')}</InputLabel>
              <Select
                value={`${sortBy}:${sortOrder}`}
                label={t('backoffice.customerManagement.filters.sortBy')}
                onChange={(e) => {
                  const [newSortBy, newSortOrder] = e.target.value.split(':');
                  setSortBy(newSortBy);
                  setSortOrder(newSortOrder);
                }}
              >
                <MenuItem value="company_name:asc">{t('backoffice.customerManagement.filters.companyNameAZ')}</MenuItem>
                <MenuItem value="company_name:desc">{t('backoffice.customerManagement.filters.companyNameZA')}</MenuItem>
                <MenuItem value="created_at:desc">{t('backoffice.customerManagement.filters.createdNewest')}</MenuItem>
                <MenuItem value="created_at:asc">{t('backoffice.customerManagement.filters.createdOldest')}</MenuItem>
                <MenuItem value="province:asc">{t('backoffice.customerManagement.filters.provinceAZ')}</MenuItem>
                <MenuItem value="credit_limit:desc">{t('backoffice.customerManagement.filters.creditLimitHigh')}</MenuItem>
                <MenuItem value="credit_limit:asc">{t('backoffice.customerManagement.filters.creditLimitLow')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Territory Filter */}
          {territories.length > 0 && (
            <Box sx={{ minWidth: 150 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('backoffice.customerManagement.filters.territory')}</InputLabel>
                <Select
                  value={selectedTerritory}
                  label={t('backoffice.customerManagement.filters.territory')}
                  onChange={(e) => setSelectedTerritory(e.target.value)}
                >
                  <MenuItem value="">{t('backoffice.customerManagement.filters.allTerritories')}</MenuItem>
                  {territories.map((territory) => (
                    <MenuItem key={territory} value={territory}>
                      {territory}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Customer Group Filter */}
          {customerGroups.length > 0 && (
            <Box sx={{ minWidth: 150 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('backoffice.customerManagement.filters.customerGroup')}</InputLabel>
                <Select
                  value={selectedGroup}
                  label={t('backoffice.customerManagement.filters.customerGroup')}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                >
                  <MenuItem value="">{t('backoffice.customerManagement.filters.allGroups')}</MenuItem>
                  {customerGroups.map((group) => (
                    <MenuItem key={group} value={group}>
                      {group}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Invoice error message */}
      {invoiceError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Invoice Error: {invoiceError}
        </Alert>
      )}
      
      {/* Backoffice Invoice error message */}
      {backofficeInvoiceError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Invoice Error: {backofficeInvoiceError}
        </Alert>
      )}
      
      {/* Backoffice Invoice info message */}
      {backofficeInvoiceInfoMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {backofficeInvoiceInfoMessage}
        </Alert>
      )}

      {/* Customer table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Epicor ID</TableCell>
                <TableCell>{t('backoffice.customerManagement.filters.portalStatus')}</TableCell>
                <TableCell>Epicor Sync</TableCell>
                <TableCell>Credit Info</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    No customers found
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => (
                  <TableRow key={customer.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">
                          {customer.company_name}
                        </Typography>
                        {customer.contact_person && (
                          <Typography variant="caption" color="text.secondary">
                            {customer.contact_person}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        {customer.email && (
                          <Typography variant="caption" display="block">
                            <Email sx={{ fontSize: 12, mr: 0.5 }} />
                            {customer.email}
                          </Typography>
                        )}
                        {customer.phone && (
                          <Typography variant="caption" display="block">
                            <Phone sx={{ fontSize: 12, mr: 0.5 }} />
                            {customer.phone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        {customer.province && (
                          <Typography variant="caption" display="block">
                            {customer.province}
                          </Typography>
                        )}
                        {customer.city && (
                          <Typography variant="caption" display="block">
                            {customer.city}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      {customer.epicor_customer_id || '-'}
                    </TableCell>

                    <TableCell>
                      {getStatusAndAction(customer)}
                    </TableCell>

                    <TableCell>
                      <SyncStatusCell
                        status={customer.epicor_sync_status}
                        lastSyncAt={customer.last_sync_at}
                        externalRef={customer.epicor_customer_id}
                        entityLabel={customer.company_name}
                      />
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography variant="caption" display="block">
                          Limit: {formatCurrency(customer.credit_limit)}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Balance: {formatCurrency(customer.outstanding_balance)}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewCustomer(customer)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>

                      {customer.portal_status === 'active' && (
                        <Tooltip title={t('backoffice.customerManagement.activation.changeContact')}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => { e.stopPropagation(); handleOpenChangeContactDialog(customer); }}
                          >
                            <SwapHoriz />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title={customer.is_active === false ? "Unblock Customer" : "Block Customer"}>
                        <IconButton
                          size="small"
                          onClick={() => handleBlockCustomer(customer)}
                          color={customer.is_active === false ? "success" : "error"}
                        >
                          <Block />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* Customer Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Customer Details: {selectedCustomer?.company_name}
        </DialogTitle>
        
        <DialogContent>
          {selectedCustomer && (
            <Box>
              <Tabs
                value={detailTabValue}
                onChange={(_, newValue) => setDetailTabValue(newValue)}
                aria-label="customer detail tabs"
              >
                <Tab label="Customer Info" />
                <Tab label={`Contacts (${customerUsers.length})`} />
                <Tab label={`Orders (${customerOrders.length})`} />
                <Tab label={`Invoices (${customerInvoices.length})`} />
              </Tabs>

              {/* Customer Info Tab */}
              <TabPanel value={detailTabValue} index={0}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <Business sx={{ mr: 1 }} />
                        Company Information
                      </Typography>
                      <Typography><strong>Company:</strong> {selectedCustomer.company_name}</Typography>
                      <Typography><strong>Contact Person:</strong> {selectedCustomer.contact_person || 'N/A'}</Typography>
                      <Typography><strong>Phone:</strong> {selectedCustomer.phone || 'N/A'}</Typography>
                      <Typography><strong>Territory:</strong> {selectedCustomer.territory || 'N/A'}</Typography>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <AccountBalance sx={{ mr: 1 }} />
                        Financial Information
                      </Typography>
                      <Typography><strong>Credit Limit:</strong> {formatCurrency(selectedCustomer.credit_limit)}</Typography>
                      <Typography><strong>Outstanding Balance:</strong> {formatCurrency(selectedCustomer.outstanding_balance)}</Typography>
                      <Typography><strong>Available Credit:</strong> {formatCurrency(selectedCustomer.available_credit)}</Typography>
                      <Typography><strong>Customer Group:</strong> {selectedCustomer.customer_group || 'N/A'}</Typography>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <LocationOn sx={{ mr: 1 }} />
                        Location Information
                      </Typography>
                      <Typography><strong>Province:</strong> {selectedCustomer.province || 'N/A'}</Typography>
                      <Typography><strong>City:</strong> {selectedCustomer.city || 'N/A'}</Typography>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <CreditCard sx={{ mr: 1 }} />
                        System Information
                      </Typography>
                      <Typography><strong>Epicor ID:</strong> {selectedCustomer.epicor_customer_id || 'N/A'}</Typography>
                      <Typography><strong>Mowilex ID:</strong> {selectedCustomer.mowilex_customer_id || 'N/A'}</Typography>
                      <Typography component="div"><strong>Status:</strong> {getStatusAndAction(selectedCustomer)}</Typography>
                      <Typography><strong>Created:</strong> {new Date(selectedCustomer.created_at).toLocaleDateString()}</Typography>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <Business sx={{ mr: 1 }} />
                        Sales Representative
                      </Typography>
                      {selectedCustomer.sales_rep ? (
                        <>
                          <Typography>
                            <strong>Name:</strong> {selectedCustomer.sales_rep.name || selectedCustomer.sales_rep.code}
                          </Typography>
                          {selectedCustomer.sales_rep.role_description && (
                            <Typography>
                              <strong>Role:</strong> {selectedCustomer.sales_rep.role_description}
                            </Typography>
                          )}
                          {selectedCustomer.sales_rep.email && (
                            <Typography>
                              <strong>Email:</strong>{' '}
                              <a href={`mailto:${selectedCustomer.sales_rep.email}`}>
                                {selectedCustomer.sales_rep.email}
                              </a>
                            </Typography>
                          )}
                          <Typography><strong>Code:</strong> {selectedCustomer.sales_rep.code}</Typography>
                        </>
                      ) : selectedCustomer.sales_rep_code ? (
                        <Typography color="text.secondary">
                          Assigned code <strong>{selectedCustomer.sales_rep_code}</strong> is not in the workforce cache yet.
                          Run a workforce sync to resolve.
                        </Typography>
                      ) : (
                        <Typography color="text.secondary">No sales rep assigned.</Typography>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </TabPanel>

              {/* Users Tab */}
              <TabPanel value={detailTabValue} index={1}>
                {selectedCustomer ? (
                  <CustomerContactsTab customerId={selectedCustomer.id} />
                ) : null}
              </TabPanel>

              {/* Orders Tab */}
              <TabPanel value={detailTabValue} index={2}>
                {detailLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {/* Source filter — client-side over the already-loaded list */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Source</InputLabel>
                        <Select
                          value={customerOrdersSourceFilter}
                          label="Source"
                          onChange={(e) => setCustomerOrdersSourceFilter(e.target.value as 'all' | 'portal' | 'epicor')}
                        >
                          <MenuItem value="all">All Sources</MenuItem>
                          <MenuItem value="portal">Online (Portal)</MenuItem>
                          <MenuItem value="epicor">Offline (Epicor)</MenuItem>
                        </Select>
                      </FormControl>
                      {(() => {
                        const filtered = customerOrders.filter((o) =>
                          customerOrdersSourceFilter === 'all'
                            ? true
                            : o.created_via === customerOrdersSourceFilter,
                        );
                        return (
                          <Typography variant="body2" color="text.secondary">
                            Showing {filtered.length} of {customerOrders.length} orders
                          </Typography>
                        );
                      })()}
                    </Box>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Order Number</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Epicor Sync</TableCell>
                            <TableCell>Items</TableCell>
                            <TableCell>Total Amount</TableCell>
                            <TableCell>Delivery Date</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {customerOrders
                            .filter((o) =>
                              customerOrdersSourceFilter === 'all'
                                ? true
                                : o.created_via === customerOrdersSourceFilter,
                            )
                            .map((order) => (
                          <TableRow key={order.id}>
                            <TableCell>{order.order_number}</TableCell>
                            <TableCell>
                              {order.created_via === 'epicor' ? (
                                <Box>
                                  <Chip
                                    label="Offline"
                                    color="default"
                                    variant="outlined"
                                    size="small"
                                  />
                                  {order.sales_rep_name && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block', mt: 0.5 }}
                                    >
                                      {order.sales_rep_name}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Chip
                                  label="Online"
                                  color="primary"
                                  variant="outlined"
                                  size="small"
                                />
                              )}
                            </TableCell>
                            <TableCell>{new Date(order.order_date).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <Chip
                                label={order.status}
                                color={order.status === 'completed' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <SyncStatusCell
                                status={order.epicor_sync_status}
                                lastSyncAt={order.last_sync_success_at}
                                lastAttemptAt={order.last_sync_attempt_at}
                                errorMessage={order.sync_error_message}
                                externalRef={order.epicor_order_num}
                                entityLabel={`Order ${order.order_number}`}
                              />
                            </TableCell>
                            <TableCell>{order.item_count}</TableCell>
                            <TableCell>{formatCurrency(order.total_amount)}</TableCell>
                            <TableCell>
                              {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell>
                              <IconButton 
                                size="small" 
                                title="View Order"
                                onClick={() => handleViewOrder(order)}
                              >
                                <Visibility />
                              </IconButton>
                              
                              {isInvoiceAvailable(order) && (
                                <Tooltip title="Download Invoice">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleDownloadInvoice(order.id)}
                                    disabled={invoiceLoading || isGenerating}
                                  >
                                    <Receipt />
                                  </IconButton>
                                </Tooltip>
                              )}
                              
                              {/* Temporarily hidden as requested */}
                              {/* <IconButton 
                                size="small" 
                                title="Edit Order Status"
                                onClick={() => handleEditOrder(order)}
                              >
                                <Edit />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                title="Cancel Order" 
                                color="error"
                                onClick={() => handleCancelOrder(order.id)}
                                disabled={order.status === 'cancelled' || order.status === 'completed'}
                              >
                                <Delete />
                              </IconButton> */}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  </>
                )}
              </TabPanel>

              {/* Invoices Tab */}
              <TabPanel value={detailTabValue} index={3}>
                {detailLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box>
                    {/* Invoice Status Filter */}
                    <Box sx={{ mb: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Filter by Status</InputLabel>
                        <Select
                          value={invoiceStatusFilter}
                          label="Filter by Status"
                          onChange={(e) => {
                            setInvoiceStatusFilter(e.target.value);
                            if (selectedCustomer) {
                              loadCustomerDetails(selectedCustomer.id);
                            }
                          }}
                        >
                          <MenuItem value="all">All Invoices</MenuItem>
                          <MenuItem value="DRAFT">Draft</MenuItem>
                          <MenuItem value="POSTED">Posted</MenuItem>
                          <MenuItem value="PAID">Paid</MenuItem>
                          <MenuItem value="CLOSED">Closed</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Invoice Number</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Epicor Sync</TableCell>
                            <TableCell>Invoice Date</TableCell>
                            <TableCell>Due Date</TableCell>
                            <TableCell>Total Amount</TableCell>
                            <TableCell>Order ID</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {customerInvoices.length > 0 ? (
                            customerInvoices.map((invoice) => (
                              <TableRow key={invoice.id}>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="medium">
                                    #{invoice.invoice_number}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={invoice.status}
                                    color={
                                      invoice.status === 'PAID' ? 'success' :
                                      invoice.status === 'POSTED' ? 'primary' :
                                      invoice.status === 'DRAFT' ? 'warning' : 'default'
                                    }
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <SyncStatusCell
                                    status={invoice.epicor_sync_status}
                                    lastSyncAt={invoice.last_sync_at}
                                    errorMessage={invoice.sync_error_message}
                                    externalRef={invoice.epicor_order_num}
                                    entityLabel={`Invoice ${invoice.invoice_number}`}
                                  />
                                </TableCell>
                                <TableCell>
                                  {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : '-'}
                                </TableCell>
                                <TableCell>
                                  {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : '-'}
                                </TableCell>
                                <TableCell>
                                  {formatCurrency(invoice.total_amount)}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="caption" color="text.secondary">
                                    {invoice.order_id}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {invoice.is_pdf_generated && (
                                    <Tooltip title="Download Invoice">
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => handleDownloadInvoiceById(invoice.id)}
                                        disabled={backofficeInvoiceLoading}
                                      >
                                        <Receipt />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  
                                  {invoice.order_id && (
                                    <Tooltip title="Generate Invoice">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleGenerateInvoiceFromOrder(invoice.order_id)}
                                        disabled={backofficeIsGenerating}
                                      >
                                        <Receipt />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                No invoices found for this customer
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </TabPanel>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDetailDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Order Items Dialog */}
      <Dialog
        open={orderItemsDialogOpen}
        onClose={() => setOrderItemsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Order Items - {selectedOrder?.order_number}
        </DialogTitle>
        
        <DialogContent>
          {orderItemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product Code</TableCell>
                    <TableCell>Product Name</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orderItems.length > 0 ? (
                    orderItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.product_code}</TableCell>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('id-ID', {
                            style: 'currency',
                            currency: 'IDR',
                          }).format(item.unit_price)}
                        </TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('id-ID', {
                            style: 'currency',
                            currency: 'IDR',
                          }).format(item.total_price)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No items found for this order
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOrderItemsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Activate Customer Dialog */}
      <Dialog
        open={activateDialogOpen}
        onClose={handleCloseActivateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAdd />
            {t('backoffice.customerManagement.activation.activateCustomer')}
          </Box>
        </DialogTitle>

        <DialogContent>
          {activateCustomer && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {/* Customer info summary */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('backoffice.customerManagement.activation.customerInfo')}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('backoffice.customerManagement.activation.companyName')}:</strong> {activateCustomer.company_name}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('backoffice.customerManagement.activation.epicorId')}:</strong> {activateCustomer.epicor_customer_id || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('backoffice.customerManagement.activation.contactPerson')}:</strong> {activateCustomer.contact_person || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('backoffice.customerManagement.activation.phone')}:</strong> {activateCustomer.phone || 'N/A'}
                </Typography>
              </Paper>

              {/* Form fields */}
              <TextField
                label={t('backoffice.customerManagement.activation.email')}
                type="email"
                value={activateEmail}
                onChange={(e) => setActivateEmail(e.target.value)}
                fullWidth
                required
                error={activateEmail.trim() === ''}
                helperText={activateEmail.trim() === '' ? t('backoffice.customerManagement.activation.emailRequired') : ''}
              />

              <TextField
                label={t('backoffice.customerManagement.activation.fullName')}
                value={activateFullName}
                onChange={(e) => setActivateFullName(e.target.value)}
                fullWidth
                required
                error={activateFullName.trim() === ''}
                helperText={activateFullName.trim() === '' ? t('backoffice.customerManagement.activation.nameRequired') : ''}
              />

              <TextField
                label={t('backoffice.customerManagement.activation.phone')}
                value={activatePhone}
                onChange={(e) => setActivatePhone(e.target.value)}
                fullWidth
              />

              {/* Delivery method */}
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  {t('backoffice.customerManagement.activation.deliveryMethod')}
                </FormLabel>
                <RadioGroup
                  row
                  value={activateDeliveryMethod}
                  onChange={(e) => setActivateDeliveryMethod(e.target.value as 'email' | 'whatsapp' | 'manual')}
                >
                  <FormControlLabel
                    value="email"
                    control={<Radio />}
                    label={t('backoffice.customerManagement.activation.deliveryEmail')}
                  />
                  <FormControlLabel
                    value="whatsapp"
                    control={<Radio />}
                    label={t('backoffice.customerManagement.activation.deliveryWhatsapp')}
                  />
                  <FormControlLabel
                    value="manual"
                    control={<Radio />}
                    label={t('backoffice.customerManagement.activation.deliveryManual')}
                  />
                </RadioGroup>
              </FormControl>

              <TextField
                label={t('backoffice.customerManagement.activation.customMessage')}
                value={activateCustomMessage}
                onChange={(e) => setActivateCustomMessage(e.target.value)}
                fullWidth
                multiline
                rows={3}
              />

              <TextField
                label={t('backoffice.customerManagement.activation.adminNotes')}
                value={activateAdminNotes}
                onChange={(e) => setActivateAdminNotes(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseActivateDialog}>
            {t('backoffice.customerManagement.activation.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleActivateCustomer}
            disabled={activating || activateEmail.trim() === '' || activateFullName.trim() === ''}
            startIcon={activating ? <CircularProgress size={16} /> : <PersonAdd />}
          >
            {activating
              ? t('backoffice.customerManagement.activation.activating')
              : t('backoffice.customerManagement.activation.activate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activation Result Dialog */}
      <Dialog
        open={activationResultDialogOpen}
        onClose={handleCloseActivationResult}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" />
            {t('backoffice.customerManagement.activation.activationSuccess')}
          </Box>
        </DialogTitle>

        <DialogContent>
          {activationResult && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Alert severity="success">
                {activationResult.message}
              </Alert>

              {/* Activation URL */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('backoffice.customerManagement.activation.activationLink')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    value={activationResult.activation_url}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                  <Tooltip title={t('backoffice.customerManagement.activation.copyLink')}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyToClipboard(activationResult.activation_url)}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>

              {/* Temporary Password */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('backoffice.customerManagement.activation.temporaryPassword')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    value={activationResult.temporary_password}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                  <Tooltip title={t('backoffice.customerManagement.activation.copyPassword')}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyToClipboard(activationResult.temporary_password)}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>

              {/* Email status */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {activationResult.email_sent ? (
                  <Chip
                    icon={<CheckCircle />}
                    label={t('backoffice.customerManagement.activation.emailSent')}
                    color="success"
                    size="small"
                  />
                ) : (
                  <Chip
                    icon={<CancelIcon />}
                    label={t('backoffice.customerManagement.activation.emailNotSent')}
                    color="warning"
                    size="small"
                  />
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            startIcon={<Download />}
            onClick={handleDownloadActivationInfo}
          >
            {t('backoffice.customerManagement.activation.download')}
          </Button>
          <Button variant="contained" onClick={handleCloseActivationResult}>
            {t('backoffice.customerManagement.activation.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Main Contact Dialog */}
      <Dialog
        open={changeContactDialogOpen}
        onClose={handleCloseChangeContactDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SwapHoriz />
            {t('backoffice.customerManagement.activation.changeContact')}
          </Box>
        </DialogTitle>

        <DialogContent>
          {changeContactCustomer && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('backoffice.customerManagement.activation.customerInfo')}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('backoffice.customerManagement.activation.companyName')}:</strong> {changeContactCustomer.company_name}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('backoffice.customerManagement.activation.epicorId')}:</strong> {changeContactCustomer.epicor_customer_id || 'N/A'}
                </Typography>
              </Paper>

              <Alert severity="info">
                {t('backoffice.customerManagement.activation.changeContactInfo')}
              </Alert>

              <TextField
                label={t('backoffice.customerManagement.activation.email')}
                type="email"
                value={changeContactEmail}
                onChange={(e) => setChangeContactEmail(e.target.value)}
                fullWidth
                required
                error={changeContactEmail.trim() === ''}
                helperText={changeContactEmail.trim() === '' ? t('backoffice.customerManagement.activation.emailRequired') : ''}
              />

              <TextField
                label={t('backoffice.customerManagement.activation.fullName')}
                value={changeContactFullName}
                onChange={(e) => setChangeContactFullName(e.target.value)}
                fullWidth
                required
                error={changeContactFullName.trim() === ''}
                helperText={changeContactFullName.trim() === '' ? t('backoffice.customerManagement.activation.nameRequired') : ''}
              />

              <TextField
                label={t('backoffice.customerManagement.activation.phone')}
                value={changeContactPhone}
                onChange={(e) => setChangeContactPhone(e.target.value)}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseChangeContactDialog}>
            {t('backoffice.customerManagement.activation.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleChangeMainContact}
            disabled={changingContact || changeContactEmail.trim() === '' || changeContactFullName.trim() === ''}
            startIcon={changingContact ? <CircularProgress size={16} /> : <SwapHoriz />}
          >
            {changingContact ? t('backoffice.customerManagement.activation.saving') : t('backoffice.customerManagement.activation.changeContact')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resend Activation Result Dialog */}
      <Dialog
        open={resendResultDialogOpen}
        onClose={() => setResendResultDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" />
            {t('backoffice.customerManagement.activation.resendSuccess')}
          </Box>
        </DialogTitle>

        <DialogContent>
          {resendResult && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Alert severity="success">
                {t('backoffice.customerManagement.activation.resendSuccess')}
              </Alert>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('backoffice.customerManagement.activation.activationLink')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    value={resendResult.activation_url}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                  />
                  <Tooltip title={t('backoffice.customerManagement.activation.copyLink')}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyToClipboard(resendResult.activation_url)}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {resendResult.email_sent ? (
                  <Chip
                    icon={<CheckCircle />}
                    label={t('backoffice.customerManagement.activation.emailSent')}
                    color="success"
                    size="small"
                  />
                ) : (
                  <Chip
                    icon={<CancelIcon />}
                    label={t('backoffice.customerManagement.activation.emailNotSent')}
                    color="warning"
                    size="small"
                  />
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={() => setResendResultDialogOpen(false)}>
            {t('backoffice.customerManagement.activation.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for copy confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default CustomerManagementPage;